// Operative Privacy Policy and Terms & Conditions — single source for public and signed-in Legal pages.

export const PRIVACY_POLICY = {
  id: "privacy",
  title: "Privacy Policy",
  intro:
    "This Privacy Policy explains what information TRADECRAFT collects, how it is used, and the choices you have. TRADECRAFT is an educational, simulation-first trading-education platform. It does not execute real-money trades and has no brokerage or exchange connectivity, so we do not hold funds, brokerage credentials or trading accounts for you.",
  sections: [
    {
      heading: "1. Information we collect",
      paragraphs: [
        "We collect the information needed to operate your account, personalize learning, and process subscriptions:",
      ],
      list: [
        "Account details — your name, email address, a securely hashed password, and whether your account was registered as an adult or a minor account.",
        "Competency progress — training missions, simulated trades, journal entries, analyses, assessments, challenges, skill and competency levels, XP, ranks and achievements.",
        "Usage telemetry — events about how you use features (for example pages and tools used, analyses started, items watched), used to operate and improve the product. This includes a product-analytics service used in the web app.",
        "Family and guardian relationships — which accounts are linked as guardian and learner, family invitations and their status, guardian capability authorizations, and an audit record of linking, unlinking and authorization changes.",
        "Subscription records — your plan, subscription status and the identifiers our payment processor (Stripe) returns. Payment card details are entered with Stripe; TRADECRAFT does not receive or store your full card number.",
        "Messages you send us — the content of any support request you submit.",
      ],
    },
    {
      heading: "2. How we use information",
      list: [
        "To create and secure your account, sign you in, and send account, security and billing messages (for example email verification and password reset).",
        "To deliver training, track demonstrated competency, and personalize what TRADECRAFT shows you next.",
        "To enforce access rules — plan entitlements, competency gates and, for minors, guardian authorization.",
        "To process subscriptions through Stripe and keep your plan status in sync.",
        "To operate, measure, troubleshoot, protect and improve the product, including rate limiting and abuse prevention.",
      ],
    },
    {
      heading: "3. What we do not sell",
      paragraphs: [
        "We do not sell your personal analytical or journal content — your theses, journal entries, analyses, simulated trades and competency history. We do not sell personal information.",
      ],
    },
    {
      heading: "4. Minors and guardian-managed accounts",
      paragraphs: [
        "Minors' data and guardian relationships receive additional care. A guardian and a learner are linked only when the learner accepts a single-use family invitation (a secure link or a short code) while signed into their own account; invitations expire after 7 days and can be revoked.",
        "A linked guardian can see the learner's progress and manage capability authorizations for that learner on the Guardian Dashboard. Guardian authorization is separate from competency: a minor needs both demonstrated competency and a guardian grant to use gated capabilities. Linking never grants administrative access.",
        "A guardian or learner can unlink at any time. Unlinking removes the guardian's access and authorizations for that learner; it does not delete or reset the learner's own progress.",
      ],
    },
    {
      heading: "5. AI and data processing",
      paragraphs: [
        "TRADECRAFT separates deterministic market evidence, which our engine calculates, from AI interpretation. When you use an AI-assisted feature, the relevant calculated market evidence and the content you submit for that feature (for example your thesis) are sent to our AI model provider (Anthropic's Claude) to generate the interpretation shown to you.",
        "Our Organic Intelligence editorial system's research step is currently disabled, and no web-research provider is used. Organic Intelligence does not use your account, journal or analytical content.",
      ],
    },
    {
      heading: "6. Security",
      paragraphs: [
        "Passwords are stored only as salted hashes. Access is controlled by authenticated sessions and server-side authorization checks, and sensitive actions are rate-limited. Your sign-in token is stored in your browser so you stay signed in. No method of transmission or storage is completely secure, and we cannot guarantee absolute security.",
      ],
    },
    {
      heading: "7. Retention",
      paragraphs: [
        "We keep account and learning information while your account is open and for as long as needed for the purposes described in this policy. Short-lived security items expire automatically — password-reset links after 30 minutes and family invitations after 7 days. If a paid subscription ends, your account returns to the Free plan and your competency history is preserved.",
      ],
    },
    {
      heading: "8. Your rights and deletion",
      paragraphs: [
        "You may request access to, correction of, or deletion of your personal information. Guardians may make these requests for a linked minor learner. We will act on verified requests, except where we must keep certain records to meet legal obligations, resolve disputes or prevent abuse. Depending on where you live, you may have additional rights under applicable law.",
      ],
    },
    {
      heading: "9. Changes to this policy",
      paragraphs: [
        "We may update this Privacy Policy as TRADECRAFT changes. The current version is always published on this page. If a change materially affects how we use your information, we will take reasonable steps to let you know.",
      ],
    },
  ],
};

export const TERMS = {
  id: "terms",
  title: "Terms & Conditions",
  intro:
    "These Terms & Conditions govern your use of TRADECRAFT. By creating an account or using TRADECRAFT, you agree to them. If you do not agree, do not use TRADECRAFT.",
  sections: [
    {
      heading: "1. Educational use only",
      paragraphs: [
        "TRADECRAFT is an educational, simulation-first platform. It provides training, analytical tools and AI-assisted market analysis for learning purposes. It does not provide financial, investment, tax or legal advice, does not tell you what to trade, does not execute real-money trades, and has no brokerage or exchange connectivity. Nothing in TRADECRAFT is a recommendation to buy or sell any asset, and no outcome is guaranteed. You are solely responsible for your own decisions.",
      ],
    },
    {
      heading: "2. Acceptable use",
      paragraphs: ["You agree to use TRADECRAFT only for lawful, educational purposes. You must not:"],
      list: [
        "attempt to access accounts, data or capabilities you are not authorized to use, or bypass competency, guardian or plan gates;",
        "interfere with, overload, scrape or reverse-engineer the service, or circumvent rate limits or security controls;",
        "misuse family invitations, or impersonate another person;",
        "use TRADECRAFT content to present yourself or others as providing personalized financial advice.",
      ],
    },
    {
      heading: "3. Your account",
      paragraphs: [
        "You are responsible for the accuracy of the information you provide, including whether you register as an adult or a minor, for keeping your password confidential, and for activity under your account. Tell us promptly if you believe your account has been compromised. Each person should use their own account.",
      ],
    },
    {
      heading: "4. Competency and guardian authorization",
      paragraphs: [
        "Access to certain capabilities depends on demonstrated competency. For minors, guardian authorization is also required for gated capabilities and is separate from competency: a guardian grant does not create competency, and competency does not replace a guardian grant. A paid plan expands what is available but never manufactures competency or authorization.",
      ],
    },
    {
      heading: "5. Subscriptions and billing",
      paragraphs: [
        "TRADECRAFT offers Free, Pro and Family plans as described on the Pricing page. A Free account never requires a card. Paid subscriptions are billed through Stripe at the price and billing interval shown to you at checkout, and renew until cancelled.",
        "Cancelling stops renewal; access continues until the end of the current billing period, after which your account returns to the Free plan with your competency history preserved. Plan features and prices may change; changes apply going forward and do not alter a billing period you have already paid for.",
      ],
    },
    {
      heading: "6. Family accounts",
      paragraphs: [
        "An adult can link a learner's existing account by sending a family invitation (secure link or short code); the learner must accept it while signed into their own account. Family relationships are separate from subscriptions and competency. The Family plan's linked-seat allowance is described on the Pricing page. A guardian who links a minor is responsible for supervising that minor's use of TRADECRAFT and for the authorizations they grant. Either party may unlink at any time; unlinking does not reset the learner's progress.",
      ],
    },
    {
      heading: "7. AI content limitations",
      paragraphs: [
        "Deterministic market evidence is calculated by our engine and is authoritative for factual values. AI interpretation and AI-assisted educational content, including Organic Intelligence articles, can be incomplete or wrong, is labelled as AI output, and never overrides deterministic facts. Market data comes from public sources and may be delayed or unavailable. Do not rely on any AI output or market data as advice or as a basis for real-money decisions.",
      ],
    },
    {
      heading: "8. Intellectual property",
      paragraphs: [
        "TRADECRAFT, including its software, curriculum, missions, analytical engines, design and content, belongs to TRADECRAFT and its licensors. We grant you a personal, non-transferable, revocable right to use TRADECRAFT for your own education under these Terms. Content you create — such as journal entries and theses — remains yours; you allow us to store and process it to operate the service for you.",
      ],
    },
    {
      heading: "9. Disclaimers and limitation of liability",
      paragraphs: [
        "TRADECRAFT is provided \"as is\" and \"as available\", without warranties of any kind, express or implied, including accuracy, availability or fitness for a particular purpose. To the maximum extent permitted by applicable law, TRADECRAFT will not be liable for any trading or investment losses, or for any indirect, incidental, special, consequential or punitive damages, arising from your use of, or inability to use, the service or any content in it. Some jurisdictions do not allow certain limitations, so some of these may not apply to you.",
      ],
    },
    {
      heading: "10. Changes and termination",
      paragraphs: [
        "We may update these Terms as TRADECRAFT changes; the current version is always published on this page, and continuing to use TRADECRAFT after an update means you accept it. You may stop using TRADECRAFT at any time. We may suspend or end access for accounts that breach these Terms or put other users or the service at risk.",
      ],
    },
  ],
};

export const LEGAL_DOCS = [TERMS, PRIVACY_POLICY];

export const LEGAL_NOTICE =
  "The Terms & Conditions and Privacy Policy on this page are the operative documents. The other sections are summaries.";
